import { useRef, useEffect } from "react";

/**
 * TiltCard — 3D tilt on hover using a single global mousemove delegate.
 *
 * All TiltCard instances share ONE mousemove listener for performance.
 * Auto-disabled on touch devices, reduced motion, and when modals are open.
 */
const TiltCard = ({
  children,
  className = "",
  tiltDegree = 6,
  glare = true,
  scale = 1.02,
  disabled = false,
}) => {
  const cardRef = useRef(null);
  const glareRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    // Skip on touch / reduced motion
    const isTouch =
      window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (isTouch || disabled) {
      card.style.transform = "";
      return;
    }

    let rafId = null;
    let mouseData = { x: 0, y: 0 };
    let isOver = false;

    const isModalOpen = () => {
      return !!document.querySelector(
        '.fixed.inset-0.z-50, [class*="z-50"].fixed.inset-0, .fixed.inset-0.bg-black\\/95'
      );
    };

    const onMouseMove = (e) => {
      if (isModalOpen()) {
        if (isOver) {
          isOver = false;
          card.style.transition = "transform 0.3s ease";
          card.style.transform = "perspective(1000px) rotateX(0) rotateY(0) scale3d(1,1,1)";
          if (glareRef.current) glareRef.current.style.background = "transparent";
        }
        return;
      }

      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const inside = x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;

      if (inside && !isOver) {
        isOver = true;
        card.style.transition = "transform 0.1s ease-out";
      }

      if (!inside) {
        if (isOver) {
          isOver = false;
          card.style.transition = "transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)";
          card.style.transform = "perspective(1000px) rotateX(0) rotateY(0) scale3d(1,1,1)";
          if (glareRef.current) glareRef.current.style.background = "transparent";
        }
        return;
      }

      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;

        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const rx = ((y - cy) / cy) * -tiltDegree;
        const ry = ((x - cx) / cx) * tiltDegree;

        card.style.transform = `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg) scale3d(${scale}, ${scale}, ${scale})`;

        if (glare && glareRef.current) {
          const gx = (x / rect.width) * 100;
          const gy = (y / rect.height) * 100;
          glareRef.current.style.background =
            `radial-gradient(circle at ${gx}% ${gy}%, rgba(212, 175, 122, 0.15), transparent 70%)`;
        }
      });
    };

    document.addEventListener("mousemove", onMouseMove, { passive: true });

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [tiltDegree, glare, scale, disabled]);

  return (
    <div
      ref={cardRef}
      className={`relative ${className}`}
      style={{ transformStyle: "preserve-3d" }}
    >
      {children}
      {glare && (
        <div
          ref={glareRef}
          className="absolute inset-0 rounded-[inherit] pointer-events-none z-10"
          style={{ mixBlendMode: "overlay", pointerEvents: "none" }}
        />
      )}
    </div>
  );
};

export default TiltCard;
